import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { UserRole } from '@/api/admin-types'
import { useAcceptBillingPlan } from '@/api/api-mutations'
import { preloadGetBillingPlan, useGetBillingPlan, useGetMe } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { EmptyState } from '@/components/base/empty-state'
import { Heading } from '@/components/base/heading'
import { LoadingSpinner } from '@/components/base/loading-spinner'
import { usePrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { StatusBadge } from '@/components/base/status-badge'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { computeTax, fromPrecision, sum, toPrecision } from '@/utils/currency'

type Money = { value: number; precision: number }
type Cadence = 'MONTHLY' | 'YEARLY' | 'ONE_TIME'

const LEGAL_BASE = 'https://www.fast-track.io/legal'

export const Route = createFileRoute('/_authenticated/settings/billing/')({
	component: BillingSettings,
	loader: async ({ context }) => await preloadGetBillingPlan(context, {}),
	staticData: { crumb: ({ context: { L } }) => L.Settings.Billing.Title() }
})

function BillingSettings() {
	const { L, locale } = useI18n()
	const B = L.Settings.Billing
	const me = useGetMe()
	const billing = useGetBillingPlan()
	const prompt = usePrompt()
	const acceptPlan = useAcceptBillingPlan()
	const [isAccepting, setIsAccepting] = useState(false)

	const isAdmin = me.data?.role === UserRole.Admin
	const data = billing.data
	const plan = data?.plan ?? null

	const formatFee = (amount: Money, currencyCode: string): string => {
		const value = fromPrecision(amount)
		try {
			return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode }).format(value)
		} catch {
			// Fall back to a plain amount + code if the currency isn't recognised by Intl.
			return `${value} ${currencyCode}`
		}
	}

	const cadenceLabel = (interval: Cadence): string => {
		if (interval === 'YEARLY') return B.Yearly()
		if (interval === 'ONE_TIME') return B.OneTime()
		return B.Monthly()
	}

	const cadenceTotalLabel = (interval: Cadence): string => {
		if (interval === 'YEARLY') return B.YearlyTotal()
		if (interval === 'ONE_TIME') return B.OneTimeTotal()
		return B.MonthlyTotal()
	}

	const handleAccept = async () => {
		if (!plan) {
			return
		}
		const confirmed = await prompt({
			title: B.AcceptConfirmTitle(),
			description: B.AcceptConfirmDescription(),
			confirmText: B.AcceptConfirmAction(),
			cancelText: L.General.Cancel()
		})
		if (!confirmed) {
			return
		}
		setIsAccepting(true)
		const result = await acceptPlan({})
		setIsAccepting(false)
		if (result.isSuccess) {
			toast.success(B.AcceptSuccess())
		} else {
			toast.error(B.AcceptError())
		}
	}

	// When a VAT rate is set, amounts are VAT-exclusive (net); break out net/VAT/gross. Otherwise the
	// configured amount is the gross and no breakdown is shown.
	const taxRate = plan?.taxRate ?? null
	const grossOf = (amount: Money): Money =>
		taxRate ? computeTax(toPrecision(amount), false, taxRate).gross : toPrecision(amount)
	const vatPercentLabel = (): string => `${Math.round(fromPrecision(taxRate as Money) * 10000) / 100}%`

	const renderAmountRows = (amount: Money, currencyCode: string) => {
		if (!taxRate) {
			return <SectionRow title={B.Amount()} value={formatFee(amount, currencyCode)} />
		}
		const breakdown = computeTax(toPrecision(amount), false, taxRate)
		return (
			<SectionRow
				title={B.Amount()}
				value={
					<div className='txt-compact-small grid grid-cols-[auto_auto] gap-x-4 gap-y-1'>
						<span />
						<span className='text-right'>{formatFee(breakdown.net, currencyCode)}</span>

						<span className='text-ui-fg-subtle'>{`${B.Vat()} ${vatPercentLabel()}`}</span>
						<span className='text-right'>{formatFee(breakdown.tax, currencyCode)}</span>

						<span className='border-t border-ui-border-base pt-1 font-medium text-ui-fg-base'>
							{B.Total()}
						</span>
						<span className='border-t border-ui-border-base pt-1 text-right font-medium text-ui-fg-base'>
							{formatFee(breakdown.gross, currencyCode)}
						</span>
					</div>
				}
			/>
		)
	}

	// Group gross amounts (base plan + line items) by cadence so monthly/yearly/one-time charges are
	// never summed together.
	const cadenceTotals = (): { interval: Cadence; total: Money }[] => {
		if (!plan) {
			return []
		}
		const entries: { interval: Cadence; gross: Money }[] = [
			{ interval: plan.interval as Cadence, gross: grossOf(plan.amount) },
			...plan.lineItems.map(item => ({ interval: item.interval as Cadence, gross: grossOf(item.amount) }))
		]
		const order: Cadence[] = ['MONTHLY', 'YEARLY', 'ONE_TIME']
		return order
			.map(interval => {
				const grosses = entries.filter(entry => entry.interval === interval).map(entry => entry.gross)
				return grosses.length > 0 ? { interval, total: sum(grosses) } : null
			})
			.filter((row): row is { interval: Cadence; total: Money } => row !== null)
	}

	return (
		<SingleColumnPage>
			<div className='space-y-6'>
				<div>
					<Heading level='h1'>{B.Title()}</Heading>
					<p className='mt-2 text-sm text-ui-fg-subtle'>{B.Description()}</p>
				</div>

				{billing.isPending ? (
					<LoadingSpinner />
				) : billing.isError ? (
					<p className='txt-compact-small text-ui-fg-subtle'>{B.LoadError()}</p>
				) : !plan ? (
					<EmptyState
						title={B.EmptyTitle()}
						description={
							<>
								{B.EmptyDescription()} {B.EmptyContactPrefix()}{' '}
								<a
									href='mailto:support@fast-track.io'
									className='font-medium text-ui-fg-interactive outline-none transition-fg hover:text-ui-fg-interactive-hover'
								>
									{B.EmptyContactLink()}
								</a>{' '}
								{B.EmptyContactSuffix()}
							</>
						}
						className='min-h-[240px]'
					/>
				) : (
					<div className='space-y-6'>
						<Container className='divide-y p-0'>
							<ContainerHeader
								heading={B.PlanHeading()}
								rightSide={
									!plan.acceptedAt && isAdmin ? (
										<Button variant='primary' onClick={handleAccept} isLoading={isAccepting}>
											{B.Accept()}
										</Button>
									) : undefined
								}
							/>
							{renderAmountRows(plan.amount, plan.currencyCode)}
							<SectionRow
								title={B.Interval()}
								value={plan.interval === 'YEARLY' ? B.Yearly() : B.Monthly()}
							/>
							<SectionRow title={B.StartDate()} value={L.Date.ShortWithDate(plan.startDate)} />
							<SectionRow
								title={B.Status()}
								value={
									plan.acceptedAt ? (
										<StatusBadge color='green'>{B.StatusAccepted()}</StatusBadge>
									) : (
										<StatusBadge color='orange'>{B.StatusPending()}</StatusBadge>
									)
								}
							/>
							{plan.acceptedAt && (
								<SectionRow
									title={B.AcceptedBy()}
									value={[plan.acceptedBy, L.Date.DateAndTime(plan.acceptedAt)]
										.filter(Boolean)
										.join(' · ')}
								/>
							)}
							{plan.note && (
								<div className='space-y-1 px-6 py-4'>
									<p className='txt-compact-small font-medium text-ui-fg-base'>{B.Note()}</p>
									<p className='txt-compact-small whitespace-pre-line text-ui-fg-subtle'>
										{plan.note}
									</p>
								</div>
							)}
						</Container>

						{plan.parameters.length > 0 && (
							<Container className='divide-y p-0'>
								<ContainerHeader heading={B.PlanDetails()} />
								{plan.parameters.map((parameter, index) => (
									<SectionRow
										key={`${parameter.label}-${index}`}
										title={parameter.label}
										value={
											parameter.values && parameter.values.length > 0 ? (
												<ul className='txt-compact-small list-disc space-y-1 pl-4'>
													{parameter.values.map((value, valueIndex) => (
														<li key={valueIndex}>{value}</li>
													))}
												</ul>
											) : (
												(parameter.value ?? '')
											)
										}
									/>
								))}
							</Container>
						)}

						{plan.lineItems.length > 0 && (
							<Container className='divide-y p-0'>
								<ContainerHeader heading={B.AdditionalFees()} />
								{plan.lineItems.map((lineItem, index) => {
									const breakdown = taxRate
										? computeTax(toPrecision(lineItem.amount), false, taxRate)
										: null
									return (
										<SectionRow
											key={`${lineItem.label}-${index}`}
											title={lineItem.label}
											value={
												<div className='text-right'>
													<div>
														{[
															formatFee(grossOf(lineItem.amount), plan.currencyCode),
															cadenceLabel(lineItem.interval as Cadence)
														].join(' · ')}
													</div>
													{breakdown && (
														<div className='text-ui-fg-subtle'>
															{`${B.Net()} ${formatFee(breakdown.net, plan.currencyCode)} · ${B.Vat()} ${formatFee(breakdown.tax, plan.currencyCode)}`}
														</div>
													)}
												</div>
											}
										/>
									)
								})}
							</Container>
						)}

						{plan.lineItems.length > 0 && (
							<Container className='divide-y p-0'>
								<ContainerHeader heading={B.TotalsHeading()} />
								{cadenceTotals().map(row => (
									<SectionRow
										key={row.interval}
										title={cadenceTotalLabel(row.interval)}
										value={formatFee(row.total, plan.currencyCode)}
									/>
								))}
							</Container>
						)}

						<Container className='divide-y p-0'>
							<ContainerHeader heading={B.AgreementHeading()} />
							{plan.agreementType === 'ENTERPRISE' ? (
								<div className='space-y-2 px-6 py-4 text-sm'>
									<p className='text-ui-fg-base'>{B.EnterpriseNote()}</p>
									{plan.enterpriseAgreementRef && (
										<p className='text-ui-fg-subtle'>
											{B.EnterpriseRefLabel()}: {plan.enterpriseAgreementRef}
										</p>
									)}
									<a
										href={`${LEGAL_BASE}/services`}
										target='_blank'
										rel='noreferrer'
										className='font-medium text-ui-fg-interactive outline-none transition-fg hover:text-ui-fg-interactive-hover'
									>
										{B.EnterpriseServicesLink()}
									</a>
								</div>
							) : (
								<div className='space-y-2 px-6 py-4 text-sm'>
									<p className='text-ui-fg-subtle'>{B.SelfServeIntro()}</p>
									<ul className='space-y-1'>
										{[
											{ label: B.PolicyPrivacy(), slug: 'privacy' },
											{ label: B.PolicyTerms(), slug: 'terms' },
											{ label: B.PolicyServices(), slug: 'services' },
											{ label: B.PolicyDpa(), slug: 'dpa' },
											{ label: B.PolicySupport(), slug: 'support' },
											{ label: B.PolicyEula(), slug: 'eula' }
										].map(policy => (
											<li key={policy.slug}>
												<a
													href={`${LEGAL_BASE}/${policy.slug}`}
													target='_blank'
													rel='noreferrer'
													className='font-medium text-ui-fg-interactive outline-none transition-fg hover:text-ui-fg-interactive-hover'
												>
													{policy.label}
												</a>
											</li>
										))}
									</ul>
								</div>
							)}
						</Container>
					</div>
				)}
			</div>
		</SingleColumnPage>
	)
}
